// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ConnectActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import Cancellation from 'share/errorHandling/Izenda.Cancellation';
import Confirmation from 'share/components/controls/Izenda.Confirmation';
import { withHandlers } from 'recompose';
import uiState from 'logic/Izenda.UiState';
import { get, assign, identity } from 'lodash/fp';

const CONFIRMATION = 'Confirmation';

const dialogActions = {
  confirmFn: props => (message, options) => confirmFn(props.dialogs, message, options),

  confirmDirtyForm: props => (isDirty, options) => {
    if (!isDirty) {
      return Promise.resolve();
    }

    const description = get('customMessage', options) || uiState.dirtyMessage;
    const confirmHandle = get('onConfirmDirtyForm', options) || identity;
    return confirmFn(props.dialogs, 'Confirmation', { description }).then(confirmHandle);
  },

  confirmMessage: props => (type, message) =>
    confirmFn(props.dialogs, 'Information', {
      type: type,
      description: message,
      showCancel: false,
      showCloseIndicator: true
    }),

  openPopup: props => (type, dialogOptions) => openPopup(props.dialogs, type, dialogOptions)
};

/**
 * Register a confirm popup
 * @param {Object} dialogs
 * @param {string} message
 * @param {Object} options
 * @returns {Promise}
 */
function confirmFn(dialogs: any, message: string, options: any): Promise {
  const unregister = dialogs.createUnregister(CONFIRMATION);
  const confirmPromise = new Promise((resolve, reject) => {
    const confirmOpts = assign(
      {
        message: message,
        onAccept: resolve,
        onReject: () => {
          reject(new Cancellation());
        }
      },
      options || {}
    );

    dialogs.registerDialog(CONFIRMATION, Confirmation, confirmOpts);
  }).finally(unregister);

  return confirmPromise;
}

/**
 * Register a popup
 * @param {Object} dialogs
 * @param {Object} type
 * @param {Object} options
 * @returns {Promise}
 */
function openPopup(dialogs: any, type: any, options: any): Promise {
  ensure(
    !options || !(options.onAccept || options.onReject),
    'onAccept and onReject properties is reserved for popup closing'
  );

  const displayName =
    type.displayName || type.$popupName || type.name || (typeof type === 'string' ? type : 'Component');

  const unregister = dialogs.createUnregister(displayName);

  const popupPromise = new Promise((resolve, reject) => {
    const popupOpts = assign(
      {
        onAccept: resolve,
        onReject: () => {
          reject(new Cancellation());
        }
      },
      options || {}
    );
    dialogs.registerDialog(displayName, type, popupOpts);
  }).finally(unregister);

  return popupPromise;
}

export default withHandlers(dialogActions);



// WEBPACK FOOTER //
// ./ui/share/enhancers/Izenda.ConnectActions.js